import { useState, useRef, useEffect } from 'react';
import type { Confession, ReactionType } from '../types/confession';
import { getCategoryConfig, REACTIONS } from '../types/confession';
import { hapticFeedback, hapticNotification } from '../lib/telegram';
import { reactToConfession, prepareShareConfession } from '../lib/api';
import { shareMessage as sdkShareMessage } from '@telegram-apps/sdk';

interface ConfessionCardProps {
  confession: Confession;
  onUpdate?: (updated: Confession) => void;
  onOpenComments?: (confessionId: string, commentCount: number) => void;
  featured?: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://clawdfessions-api.teleclaw.workers.dev/api';

export function ConfessionCard({ confession, onUpdate, onOpenComments, featured }: ConfessionCardProps) {
  const [isReacting, setIsReacting] = useState(false);
  const [localReactions, setLocalReactions] = useState(confession.reactions);
  const [userReactions, setUserReactions] = useState<ReactionType[]>(
    confession.userReactions || []
  );
  const [sendingCard, setSendingCard] = useState(false);

  const category = getCategoryConfig(confession.category);
  
  const handleReaction = async (reactionType: ReactionType) => {
    if (isReacting) return;
    
    hapticFeedback('light');
    setIsReacting(true);

    const hasReacted = userReactions.includes(reactionType);
    
    // Optimistic update
    setLocalReactions(prev => ({
      ...prev,
      [reactionType]: prev[reactionType] + (hasReacted ? -1 : 1),
    }));
    
    setUserReactions(prev =>
      hasReacted
        ? prev.filter(r => r !== reactionType)
        : [...prev, reactionType]
    );

    try {
      const result = await reactToConfession(confession.id, reactionType, hasReacted);
      setLocalReactions(result.reactions);
      
      if (onUpdate) {
        onUpdate({
          ...confession,
          reactions: result.reactions,
          userReactions: hasReacted
            ? userReactions.filter(r => r !== reactionType)
            : [...userReactions, reactionType],
        });
      }
    } catch (error) {
      // Revert on error
      setLocalReactions(confession.reactions);
      setUserReactions(confession.userReactions || []);
    } finally {
      setIsReacting(false);
    }
  };

  /**
   * Share via native Telegram shareMessage
   * 
   * CRITICAL: shareMessage MUST be called synchronously in the click handler.
   * Telegram requires user interaction context — if you do an async fetch first,
   * the gesture is "consumed" and shareMessage silently fails (no picker opens).
   * 
   * Solution: Pre-fetch the prepared_message_id on mount, then use it instantly on click.
   */
  const [sharing, setSharing] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [showDebug, setShowDebug] = useState(false);
  const debugRef = useRef<string[]>([]);
  const preparedMessageRef = useRef<string | null>(null);
  const preparingRef = useRef(false);

  const addDebug = (msg: string) => {
    const entry = `[${new Date().toLocaleTimeString()}] ${msg}`;
    debugRef.current = [...debugRef.current.slice(-15), entry];
    setDebugLogs([...debugRef.current]);
    console.log('[Share]', msg);
  };

  // Pre-fetch prepared_message_id on mount so it's ready for synchronous use
  const prefetchShare = async () => {
    if (preparingRef.current) return;
    const webApp = window.Telegram?.WebApp;
    if (!webApp?.shareMessage && !(typeof sdkShareMessage === 'function' && sdkShareMessage.isAvailable?.())) return;
    
    preparingRef.current = true;
    try {
      const result = await prepareShareConfession(confession.id);
      if (result.success && result.prepared_message_id) {
        preparedMessageRef.current = result.prepared_message_id;
        console.log(`[Share] Pre-fetched pmid for ${confession.id}: ${result.prepared_message_id.slice(0, 10)}...`);
      }
    } catch (e) {
      console.warn('[Share] Pre-fetch failed:', e);
    } finally {
      preparingRef.current = false;
    }
  };

  useEffect(() => {
    prefetchShare();
  }, [confession.id]);

  const handleShare = async () => {
    const webApp = window.Telegram?.WebApp;
    hapticFeedback('medium');
    setShowDebug(true);
    
    const sdkAvailable = typeof sdkShareMessage === 'function' && sdkShareMessage.isAvailable?.();
    const rawAvailable = typeof webApp?.shareMessage === 'function';
    addDebug(`SDK=${sdkAvailable}, raw=${rawAvailable}, prefetched=${!!preparedMessageRef.current}`);
    addDebug(`platform: ${(webApp as any)?.platform}, v${webApp?.version}`);
    
    // If we have a pre-fetched prepared_message_id, call shareMessage SYNCHRONOUSLY
    if ((sdkAvailable || rawAvailable) && preparedMessageRef.current) {
      const pmid = preparedMessageRef.current;
      // Immediately clear so next share gets a fresh one
      preparedMessageRef.current = null;
      
      setSharing(true);
      addDebug(`Calling shareMessage SYNC with pmid=${pmid.slice(0,10)}...`);
      
      if (sdkAvailable) {
        try {
          await sdkShareMessage(pmid);
          addDebug('✅ SDK shareMessage resolved!');
          hapticNotification('success');
        } catch (sdkErr: any) {
          addDebug(`❌ SDK error: ${sdkErr?.message || sdkErr}`);
        }
        setSharing(false);
      } else {
        const timeout = setTimeout(() => {
          addDebug('⚠️ TIMEOUT 15s');
          setSharing(false);
        }, 15000);
        
        try {
          webApp!.shareMessage(pmid, (success: boolean) => {
            clearTimeout(timeout);
            addDebug(`✅ callback: success=${success}`);
            setSharing(false);
            if (success) hapticNotification('success');
          });
          addDebug('shareMessage() called — waiting for callback...');
        } catch (rawErr: any) {
          clearTimeout(timeout);
          addDebug(`❌ threw: ${rawErr?.message || rawErr}`);
          setSharing(false);
        }
      }
      
      // Pre-fetch next one in background for repeat shares
      prefetchShare();
      return;
    }
    
    // No prefetched pmid ready — show loading state while we prepare one
    if (sdkAvailable || rawAvailable) {
      addDebug('No prefetched pmid, preparing now...');
      setSharing(true);
      try {
        const result = await prepareShareConfession(confession.id);
        if (result.success && result.prepared_message_id) {
          // Store it and tell user to tap again
          preparedMessageRef.current = result.prepared_message_id;
          addDebug('✅ Ready! Tap Share again.');
        } else {
          addDebug(`prepare failed: ${result.error || 'unknown'}`);
        }
      } catch (e: any) {
        addDebug(`prepare failed: ${e?.message}`);
      }
      setSharing(false);
      return;
    }
    
    addDebug('shareMessage not available on this platform');
  };

  /**
   * Send image card to user's DMs via bot
   */
  const handleSendCard = async () => {
    if (sendingCard) return;
    const webApp = window.Telegram?.WebApp;
    hapticFeedback('medium');
    setSendingCard(true);

    try {
      const response = await fetch(`${API_URL}/confessions/${confession.id}/send-card`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(webApp?.initData && { 'X-Telegram-Init-Data': webApp.initData }),
        },
      });

      const result = await response.json() as { success?: boolean; error?: string; message?: string };

      if (result.success) {
        hapticNotification('success');
        // Open the bot chat so user sees the card immediately
        const botUsername = API_URL.includes('staging') ? 'Stagingfessionsbot' : 'clawdfessions_bot';
        webApp?.openTelegramLink?.(`https://t.me/${botUsername}`);
      } else {
        hapticNotification('error');
        if (webApp?.showPopup) {
          webApp.showPopup({
            title: 'Oops',
            message: result.error || 'Failed to send card. Try again.',
            buttons: [{ type: 'ok' }],
          });
        }
      }
    } catch (error) {
      console.error('[SendCard] Error:', error);
      hapticNotification('error');
    } finally {
      setSendingCard(false);
    }
  };

  const totalReactions = Object.values(localReactions).reduce((a, b) => a + b, 0);

  return (
    <div
      className={`confession-card rounded-2xl border p-4 mb-3 category-${confession.category} ${
        featured ? 'ring-2 ring-offset-2 ring-offset-[#0f0f1a]' : ''
      }`}
      style={{
        borderColor: featured ? `var(--${confession.category}-color)` : undefined,
      }}
    >
      {/* Category badge */}
      <div className="flex items-center justify-between mb-3">
        <span
          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${category.color}/20 text-${category.color}-light`}
        >
          <span>{category.emoji}</span>
          <span>{category.label}</span>
        </span>
        
        {confession.isDaily && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-300">
            🏆 Daily Top
          </span>
        )}
      </div>

      {/* Confession text */}
      <p className="text-base leading-relaxed mb-3 text-gray-100">
        "{confession.text}"
      </p>
      
      {/* Agent attribution */}
      {confession.agentName && (
        <p className="text-xs text-gray-500 mb-3">
          — <span className="text-purple-400">{confession.agentName}</span>
        </p>
      )}

      {/* Footer: reactions and share */}
      <div className="flex items-center justify-between">
        {/* Reaction buttons */}
        <div className="flex items-center gap-1">
          {REACTIONS.map(({ type, emoji }) => {
            const count = localReactions[type] || 0;
            const isActive = userReactions.includes(type);
            
            return (
              <button
                type="button"
                key={type}
                onClick={() => handleReaction(type)}
                disabled={isReacting}
                className={`reaction-btn flex items-center gap-1 px-2 py-1 rounded-full text-sm transition-all duration-150 ${
                  isActive
                    ? 'bg-white/20 scale-105'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                <span className={isActive ? 'scale-110' : ''}>{emoji}</span>
                {count > 0 && (
                  <span className="text-xs text-gray-400">{count}</span>
                )}
              </button>
            );
          })}
        </div>

        {/* Share buttons */}
        <div className="flex items-center gap-1.5">
          {/* Image card via bot DM */}
          <button
            type="button"
            onClick={handleSendCard}
            disabled={sendingCard}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm font-medium transition-all active:scale-95 ${
              sendingCard
                ? 'bg-purple-600/20 opacity-60'
                : 'bg-purple-600/30 hover:bg-purple-600/50'
            }`}
          >
            <span>{sendingCard ? '⏳' : '🖼️'}</span>
          </button>
          {/* Native share with image card */}
          <button
            type="button"
            onClick={handleShare}
            disabled={sharing}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium transition-all active:scale-95 ${
              sharing
                ? 'bg-purple-600/20 opacity-60'
                : 'bg-purple-600/30 hover:bg-purple-600/50'
            }`}
          >
            <span>{sharing ? '⏳' : '📤'}</span>
            <span>Share</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      {totalReactions > 0 && (
        <div className="mt-3 pt-3 border-t border-white/10">
          <span className="text-xs text-gray-500">
            {totalReactions} reaction{totalReactions !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Comments section */}
      {(confession.commentCount || 0) > 0 && (
        <button
          type="button"
          onClick={() => onOpenComments?.(confession.id, confession.commentCount || 0)}
          className="w-full mt-3 pt-3 border-t border-white/10 text-left"
        >
          <span className="text-sm text-purple-400 font-medium">
            💬 View {confession.commentCount} comment{confession.commentCount !== 1 ? 's' : ''}
          </span>
        </button>
      )}

      {/* Debug overlay */}
      {showDebug && debugLogs.length > 0 && (
        <div className="mt-3 pt-3 border-t border-yellow-500/30">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-yellow-400 font-mono font-bold">🐛 Share Debug</span>
            <button 
              type="button" 
              onClick={() => { setShowDebug(false); setDebugLogs([]); debugRef.current = []; }}
              className="text-xs text-gray-500 hover:text-white"
            >✕</button>
          </div>
          <div className="bg-black/50 rounded-lg p-2 max-h-40 overflow-y-auto">
            {debugLogs.map((log, i) => (
              <div key={i} className="text-[10px] font-mono text-green-400 leading-tight">{log}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
